import { queryOptions } from '@tanstack/react-query';
import { FetchError } from 'ofetch';
import { z } from 'zod';
import { apiClient } from '@/lib/api-client';
import { SUPPORTED_CHAINS } from '@/lib/constants';
import { i18n } from '@/lib/i18n';

export const CrossChainRouteSchema = z.object({
  fromChain: z.literal(SUPPORTED_CHAINS), // e.g. "BSC"
  toChain: z.literal(SUPPORTED_CHAINS), // e.g. "D9"
  enabled: z.boolean(),
  minAmount: z.number(),
  maxAmount: z.number().default(Number.MAX_SAFE_INTEGER),
  adjustmentBasisPoints: z.number(),
  autoEgress: z.boolean(),
  maxAutoAmount: z.number().default(Number.MAX_SAFE_INTEGER),
  defaultContractAddress: z.string(),
});

export type CrossChainRoute = z.infer<typeof CrossChainRouteSchema>;

export const RouteInformationSchema = z.object({
  route: z.string(), // e.g., "BSC-D9"
  from: z.literal(SUPPORTED_CHAINS),
  to: z.literal(SUPPORTED_CHAINS),
  enabled: z.boolean(),
  limits: z.object({
    min: z.number(),
    max: z.number(),
  }),
  adjustmentBasisPoints: z.number(),
  defaultContractAddress: z.string(),
  fromChain: z.object({
    name: z.literal(SUPPORTED_CHAINS),
    usdtDecimals: z.number(),
  }),
  toChain: z.object({
    name: z.literal(SUPPORTED_CHAINS),
    usdtDecimals: z.number(),
  }),
});

export type RouteInformation = z.infer<typeof RouteInformationSchema>;

export const crossChainRoutesQueryOptions = queryOptions({
  queryKey: ['cross-chain-routes'],
  queryFn: () => {
    return apiClient<CrossChainRoute[]>('/api/v2/routes').then((data) =>
      data
        .map((item) => CrossChainRouteSchema.safeParse(item).data)
        .filter((item) => item !== undefined),
    );
  },
});

export const routeInformationQueryOptions = (
  fromChain: string,
  toChain: string,
) =>
  queryOptions({
    queryKey: ['route-information', fromChain, toChain],
    queryFn: () => {
      return apiClient<RouteInformation>(
        `/api/v2/routes/${fromChain}/${toChain}`,
      ).then((data) => RouteInformationSchema.safeParse(data).data);
    },
  });

export const tokenPriceQueryOptions = (chain: string, token: string) =>
  queryOptions({
    queryKey: ['token-price', chain, token],
    queryFn: () => fetch(''),
  });

export const transferIdQueryOptions = (receiver: string) =>
  queryOptions({
    queryKey: ['transfer-id', receiver],
    queryFn: () => {
      return apiClient<{ transferId: string }>(
        `/api/v2/transfers/id/${receiver}`,
      ).then((data) => data.transferId);
    },
  });

export const transferIdInformationQueryOptions = (transferId: string) =>
  queryOptions({
    queryKey: ['transfer-id-information', transferId],
    queryFn: async () => {
      try {
        // oxlint-disable-next-line no-unused-vars
        const result = await apiClient<TransferStatus>(
          `/api/v2/transfers/${transferId}`,
        );
        // if (result.adminApprovalStatus === 'pending') {
        //   return true;
        // }
        if (result.adminApprovalStatus === 'approved') {
          return true;
        }
        if (result.adminApprovalStatus === 'pending') {
          throw new Error(
            i18n.t('errors.transferInProgressWithDetails', {
              fromChain: result.fromChain,
              toChain: result.toChain,
              requestId: result.requestId,
            }),
          );
        }

        return false;
      } catch (error) {
        if (
          error instanceof Error &&
          typeof error.cause === 'object' &&
          'code' in error.cause! &&
          error.cause.code === 'NOT_FOUND'
        ) {
          return true;
        }
        throw error;
      }
    },
  });

export const usdtBalanceQueryOptions = (
  chain: string,
  address: string | undefined,
) =>
  queryOptions({
    queryKey: ['usdt-balance', chain, address],
    queryFn: async () => {
      if (!address) {
        return '0';
      }
      // This will be implemented with actual contract calls
      return '0';
    },
    enabled: !!address,
  });

export interface TransferStatus {
  transferId: string;
  adminApprovalStatus: 'pending' | 'approved' | 'rejected';
  requestId: string;
  fromChain: string;
  toChain: string;
  fromAddress: string;
  toAddress: string;
  amount: number;
  createdAt: string;
  updatedAt: string;
}

export const userTransferStatusQueryOptions = (userId?: string) =>
  queryOptions({
    queryKey: ['transfer-id-full-information', userId],
    queryFn: async () => {
      try {
        const { transferId } = await apiClient<{ transferId: string }>(
          `/api/v2/transfers/id/${userId}`,
          { retry: false },
        );
        const response = await apiClient<TransferStatus>(
          `/api/v2/transfers/${transferId}`,
          { retry: false },
        );
        return response;
      } catch (error) {
        if (error instanceof FetchError) {
          if (
            error.cause &&
            typeof error.cause === 'object' &&
            'code' in error.cause &&
            error.cause.code === 'NOT_FOUND'
          ) {
            return undefined;
          }
        }
        throw error;
      }
    },
    refetchInterval(query) {
      if (query.state.fetchFailureReason) {
        return 3000;
      }
      if (query.state.fetchFailureCount > 3) {
        return 10000;
      }
      return 1000;
    },
    enabled: !!userId,
  });
